/**
 * A face for every player.
 *
 * Drawn rather than downloaded. A photograph would mean an upload flow, a
 * moderation problem and a file per player on a phone connection; a letter in a
 * circle is what this replaced and it reads as a placeholder because it is one.
 * These are drawn from the name, deterministically, so a player has the same
 * face every time and the people around them learn it.
 *
 * The set is small on purpose — six characters over twelve palettes. A table
 * seats six, and what matters is that no two people at one table look alike at
 * a glance, not that a thousand players have a unique portrait. Colour does
 * most of that work; the character does the rest.
 *
 * Everything is inline SVG. Nothing is fetched, which keeps it working under
 * the page's own strict rules about where content may come from, and keeps a
 * table on a phone from opening six connections to draw six heads.
 */

const PALETTES: readonly { skin: string; hair: string; cloth: string; back: string }[] = [
  { skin: '#e8b98c', hair: '#2c1f18', cloth: '#7b2d3b', back: '#3a2430' },
  { skin: '#c98d5d', hair: '#1a1410', cloth: '#1f5d52', back: '#173029' },
  { skin: '#f0c9a4', hair: '#5a3a20', cloth: '#2a4a7a', back: '#1c2c46' },
  { skin: '#8d5b3a', hair: '#120d0a', cloth: '#6b5220', back: '#332a15' },
  { skin: '#e5b085', hair: '#6e6a66', cloth: '#3f3f52', back: '#26262f' },
  { skin: '#d9a273', hair: '#3b1f0f', cloth: '#245e34', back: '#16301f' },
  { skin: '#f2d0b0', hair: '#8a5a2a', cloth: '#5a2a6b', back: '#2e1a37' },
  { skin: '#a86a42', hair: '#241a12', cloth: '#8a4a1c', back: '#3a2413' },
  { skin: '#efc7a2', hair: '#111', cloth: '#1e4f6b', back: '#152b39' },
  { skin: '#caa07a', hair: '#4a2c14', cloth: '#6b2a2a', back: '#331717' },
  { skin: '#e0ae82', hair: '#2a2a2a', cloth: '#2f6b62', back: '#183530' },
  { skin: '#b98055', hair: '#1d1208', cloth: '#4a3a7a', back: '#241d3a' },
];

/**
 * Two numbers from a name: which character, which palette.
 *
 * Different multipliers so the two do not move together — otherwise every
 * player with character three would also have palette three, and half the
 * variety would be wasted.
 */
function hashOf(name: string): { character: number; palette: number } {
  let a = 2166136261;
  let b = 5381;
  for (let i = 0; i < name.length; i += 1) {
    a = Math.imul(a ^ name.charCodeAt(i), 16777619) >>> 0;
    b = ((b * 33) ^ name.charCodeAt(i)) >>> 0;
  }
  return { character: a % 6, palette: b % PALETTES.length };
}

/** The bits that differ between the six: hair, and whether they have a beard. */
function features(character: number, hair: string): JSX.Element {
  switch (character) {
    case 0: // cropped
      return <path d="M15 20c0-6 4-9 9-9s9 3 9 9c0 1-1 2-2 1-1-3-4-4-7-4s-6 1-7 4c-1 1-2 0-2-1z" fill={hair} />;
    case 1: // long, tied back
      return (
        <>
          <path d="M14 22c0-7 4-11 10-11s10 4 10 11c0 2-2 2-2 0 0-4-3-6-8-6s-8 2-8 6c0 2-2 2-2 0z" fill={hair} />
          <path d="M33 21c3 2 4 7 3 11-2-3-3-6-3-11z" fill={hair} />
        </>
      );
    case 2: // side parting
      return <path d="M15 21c1-6 5-10 10-10 4 0 8 2 9 7-4-2-7-2-11 0-3 1-6 3-8 3z" fill={hair} />;
    case 3: // cap
      return (
        <>
          <path d="M13 21c0-6 5-10 11-10s11 4 11 10z" fill={hair} />
          <rect x="11" y="20" width="26" height="3" rx="1.5" fill={hair} />
        </>
      );
    case 4: // bald with a beard
      return <path d="M16 27c1 7 4 10 8 10s7-3 8-10c-2 4-5 5-8 5s-6-1-8-5z" fill={hair} opacity=".85" />;
    default: // curls
      return (
        <>
          <circle cx="18" cy="18" r="5" fill={hair} />
          <circle cx="24" cy="15" r="5.5" fill={hair} />
          <circle cx="30" cy="18" r="5" fill={hair} />
        </>
      );
  }
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  const { character, palette } = hashOf(name || '?');
  const colours = PALETTES[palette] as (typeof PALETTES)[number];

  return (
    <svg
      className={className ?? 'avatar'}
      viewBox="0 0 48 48"
      role="img"
      aria-label={name}
      // The name in a title, so hovering a face tells you whose it is — which
      // matters once the name beside it has been shortened to fit a phone.
    >
      <title>{name}</title>
      <defs>
        <clipPath id={`clip-${palette}-${character}`}>
          <circle cx="24" cy="24" r="24" />
        </clipPath>
      </defs>
      <g clipPath={`url(#clip-${palette}-${character})`}>
        <rect width="48" height="48" fill={colours.back} />
        {/* Shoulders, so the head is a portrait rather than a floating ball. */}
        <path d="M6 48c0-8 8-13 18-13s18 5 18 13z" fill={colours.cloth} />
        <path d="M20 33h8v6h-8z" fill={colours.skin} />
        <ellipse cx="24" cy="24" rx="9.5" ry="11" fill={colours.skin} />
        <ellipse cx="20.5" cy="24" rx="1.1" ry="1.4" fill="#1b1410" />
        <ellipse cx="27.5" cy="24" rx="1.1" ry="1.4" fill="#1b1410" />
        <path d="M21.5 29c1.5 1.2 3.5 1.2 5 0" stroke="#1b1410" strokeWidth="1" strokeLinecap="round" fill="none" opacity=".7" />
        {features(character, colours.hair)}
      </g>
      <circle cx="24" cy="24" r="23" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="2" />
    </svg>
  );
}
